import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface RatingDisplayProps {
  rating: number | null;
  totalReviews: number;
  size?: "sm" | "md" | "lg";
  showReviewCount?: boolean;
  onClick?: () => void;
  className?: string;
}

export default function RatingDisplay({ 
  rating, 
  totalReviews, 
  size = "md", 
  showReviewCount = true,
  onClick,
  className = ""
}: RatingDisplayProps) {
  if (!rating || totalReviews === 0) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground">No ratings yet</span>
      </div>
    );
  }

  const starSize = {
    sm: "w-3 h-3",
    md: "w-4 h-4", 
    lg: "w-5 h-5"
  }[size];

  const textSize = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  }[size];

  const isClickable = onClick && totalReviews > 0;

  return (
    <div 
      className={`flex items-center gap-2 ${isClickable ? 'cursor-pointer hover:opacity-75 transition-opacity' : ''} ${className}`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
    >
      <div className="flex items-center gap-1">
        <Star className={`${starSize} fill-yellow-400 text-yellow-400`} />
        <span className={`${textSize} font-medium`}>
          {rating.toFixed(1)}
        </span>
      </div>
      {showReviewCount && (
        <Badge variant="secondary" className={textSize}>
          {totalReviews} review{totalReviews !== 1 ? 's' : ''}
        </Badge>
      )}
    </div>
  );
}